import Vue from "vue";
import { forumService } from "../services/forum.service";

function feedKey(scope, entityId) {
  return entityId != null ? `${scope}:${entityId}` : scope;
}

const state = {
  boards: {},       // key → { board, isLoading }
  feeds: {},        // key → { threads[], nextPageToken, isLoading, isReloading }
  threadsById: {},
  visibleBoards: { items: [], isLoading: false },
  isLoading: false,
};

const getters = {
  boardForKey: (state) => (key) => state.boards[key] || null,
  feedForKey: (state) => (key) =>
    state.feeds[key] || {
      threads: [],
      nextPageToken: null,
      isLoading: false,
      isReloading: false,
    },
  threadById: (state) => (id) => state.threadsById[id] || null,
  visibleBoards: (state) => state.visibleBoards,
};

const actions = {
  async fetchVisibleBoards({ commit }) {
    commit("setVisibleBoardsLoading", true);
    try {
      const items = await forumService.fetchVisibleBoards();
      commit("setVisibleBoards", items || []);
      return items;
    } finally {
      commit("setVisibleBoardsLoading", false);
    }
  },

  async fetchBoard({ commit }, { scope, entityId = null } = {}) {
    const key = feedKey(scope, entityId);
    commit("setBoardLoading", { key, isLoading: true });
    try {
      const board = await forumService.fetchBoard(scope, entityId);
      commit("setBoard", { key, board });
      return board;
    } finally {
      commit("setBoardLoading", { key, isLoading: false });
    }
  },

  async fetchFeed(
    { commit, state },
    { scope, entityId = null, searchQuery = null, sortBy = null, reload = false } = {}
  ) {
    const key = feedKey(scope, entityId);
    const current = state.feeds[key];
    if (current?.isLoading) return;

    // Order matters: resetFeed replaces the whole entry. Calling it
    // AFTER setFeedLoading/setFeedReloading would wipe those flags back to false.
    if (reload) {
      commit("resetFeed", { key });
      commit("setFeedReloading", { key, isReloading: true });
    }
    commit("setFeedLoading", { key, isLoading: true });

    try {
      const token = reload ? null : current?.nextPageToken || null;
      const { payload, nextPageToken } = await forumService.fetchFeed({
        scope,
        entityId,
        nextPageToken: token,
        searchQuery,
        sortBy,
      });

      if (reload) {
        commit("setFeedThreads", { key, threads: payload });
        commit("setFeedReloading", { key, isReloading: false });
      } else {
        commit("appendFeedThreads", { key, threads: payload });
      }
      commit("setFeedNextPageToken", { key, nextPageToken });
      return payload;
    } finally {
      commit("setFeedLoading", { key, isLoading: false });
    }
  },

  async fetchThreadById({ commit }, id) {
    commit("setLoading", true);
    try {
      const thread = await forumService.fetchThreadById(id);
      commit("setThread", thread);
      return thread;
    } finally {
      commit("setLoading", false);
    }
  },

  async addThread({ commit }, { boardScope, entityId, title, body, attachmentIds = [] }) {
    commit("setLoading", true);
    try {
      const thread = await forumService.addThread({
        boardScope,
        entityId,
        title,
        body,
        attachmentIds,
      });
      const key = feedKey(boardScope, entityId);
      commit("prependFeedThread", { key, thread });
      commit("setThread", thread);
      return thread;
    } finally {
      commit("setLoading", false);
    }
  },

  async updateThread({ commit }, payload) {
    commit("setLoading", true);
    try {
      const thread = await forumService.updateThread(payload);
      commit("setThread", thread);
      commit("replaceThreadInFeeds", thread);
      return thread;
    } finally {
      commit("setLoading", false);
    }
  },

  async deleteThread({ commit }, id) {
    commit("setLoading", true);
    try {
      const ok = await forumService.deleteThread(id);
      if (ok) commit("removeThreadFromFeeds", id);
      return ok;
    } finally {
      commit("setLoading", false);
    }
  },

  async setBoardAllowStudentThreads({ commit }, { boardScope, entityId, isAllowed }) {
    const ok = await forumService.setBoardAllowStudentThreads({ boardScope, entityId, isAllowed });
    if (ok) {
      commit("applyBoardAllowStudentThreads", { key: feedKey(boardScope, entityId), isAllowed });
    }
    return ok;
  },

  async createBoard({ commit }, { boardScope, entityId }) {
    const board = await forumService.createBoard({ boardScope, entityId });
    if (board) {
      commit("setBoard", { key: feedKey(boardScope, entityId), board });
    }
    return board;
  },

  async setBoardStatus({ commit }, { boardScope, entityId, status }) {
    const board = await forumService.setBoardStatus({ boardScope, entityId, status });
    if (board) {
      commit("setBoard", { key: feedKey(boardScope, entityId), board });
    }
    return board;
  },

  async setThreadPinned({ commit }, payload) {
    const ok = await forumService.setThreadPinned(payload);
    if (ok) commit("applyPinnedChange", payload);
    return ok;
  },

  // Cross-module entry points called by the interactions module to keep
  // feed-level thread counters in sync with /interactions/* activity.

  applyReactionResult({ commit }, result) {
    commit("applyReactionResult", result);
  },

  bumpThreadCommentCount({ commit }, payload) {
    commit("bumpThreadCommentCount", payload);
  },
};

const mutations = {
  setLoading(state, isLoading) {
    Vue.set(state, "isLoading", isLoading);
  },

  // ---------- Visible boards (admin/trainer table) ----------
  setVisibleBoards(state, items) {
    Vue.set(state.visibleBoards, "items", items);
  },
  setVisibleBoardsLoading(state, isLoading) {
    Vue.set(state.visibleBoards, "isLoading", isLoading);
  },

  // ---------- Boards ----------
  setBoard(state, { key, board }) {
    const entry = { ...(state.boards[key] || {}), board };
    Vue.set(state.boards, key, entry);
  },
  setBoardLoading(state, { key, isLoading }) {
    const entry = { ...(state.boards[key] || {}), isLoading };
    Vue.set(state.boards, key, entry);
  },
  applyBoardAllowStudentThreads(state, { key, isAllowed }) {
    const entry = state.boards[key];
    if (!entry || !entry.board) return;
    Vue.set(state.boards, key, {
      ...entry,
      board: { ...entry.board, isStudentThreadsAllowed: isAllowed },
    });
  },

  // ---------- Feeds ----------
  resetFeed(state, { key }) {
    Vue.set(state.feeds, key, {
      threads: [],
      nextPageToken: null,
      isLoading: false,
      isReloading: false,
    });
  },
  setFeedThreads(state, { key, threads }) {
    const entry = { ...(state.feeds[key] || {}), threads };
    Vue.set(state.feeds, key, entry);
  },
  appendFeedThreads(state, { key, threads }) {
    const current = state.feeds[key]?.threads || [];
    const entry = { ...(state.feeds[key] || {}), threads: [...current, ...threads] };
    Vue.set(state.feeds, key, entry);
  },
  prependFeedThread(state, { key, thread }) {
    const current = state.feeds[key]?.threads || [];
    const entry = { ...(state.feeds[key] || {}), threads: [thread, ...current] };
    Vue.set(state.feeds, key, entry);
  },
  setFeedNextPageToken(state, { key, nextPageToken }) {
    const entry = { ...(state.feeds[key] || {}), nextPageToken };
    Vue.set(state.feeds, key, entry);
  },
  setFeedLoading(state, { key, isLoading }) {
    const entry = { ...(state.feeds[key] || {}), isLoading };
    Vue.set(state.feeds, key, entry);
  },
  setFeedReloading(state, { key, isReloading }) {
    const entry = { ...(state.feeds[key] || {}), isReloading };
    Vue.set(state.feeds, key, entry);
  },
  replaceThreadInFeeds(state, thread) {
    Object.keys(state.feeds).forEach((feedScopeKey) => {
      const currentFeed = state.feeds[feedScopeKey];
      if (!currentFeed?.threads) return;
      const threadIndex = currentFeed.threads.findIndex((existingThread) => existingThread.id === thread.id);
      if (threadIndex !== -1) {
        const updatedThreads = [...currentFeed.threads];
        updatedThreads[threadIndex] = { ...updatedThreads[threadIndex], ...thread };
        Vue.set(state.feeds, feedScopeKey, { ...currentFeed, threads: updatedThreads });
      }
    });
  },
  removeThreadFromFeeds(state, threadId) {
    Object.keys(state.feeds).forEach((feedScopeKey) => {
      const currentFeed = state.feeds[feedScopeKey];
      if (!currentFeed?.threads) return;
      const remainingThreads = currentFeed.threads.filter((existingThread) => existingThread.id !== threadId);
      if (remainingThreads.length !== currentFeed.threads.length) {
        Vue.set(state.feeds, feedScopeKey, { ...currentFeed, threads: remainingThreads });
      }
    });
  },

  // ---------- Threads detail ----------
  setThread(state, thread) {
    Vue.set(state.threadsById, thread.id, thread);
  },

  // ---------- Cross-module sync ----------
  applyReactionResult(state, { entityID, hasReacted, reactionCount } = {}) {
    // Update the thread detail cache.
    const thread = state.threadsById[entityID];
    if (thread) {
      Vue.set(state.threadsById, entityID, { ...thread, hasReacted, reactionCount });
    }
    // Update any feed entry that has this thread.
    Object.keys(state.feeds).forEach((feedScopeKey) => {
      const feed = state.feeds[feedScopeKey];
      if (!feed?.threads) return;
      const threadIndex = feed.threads.findIndex((thread) => thread.id === entityID);
      if (threadIndex !== -1) {
        const updatedThreads = [...feed.threads];
        updatedThreads[threadIndex] = { ...updatedThreads[threadIndex], hasReacted, reactionCount };
        Vue.set(state.feeds, feedScopeKey, { ...feed, threads: updatedThreads });
      }
    });
  },
  bumpThreadCommentCount(state, { threadId, delta }) {
    const cachedThread = state.threadsById[threadId];
    if (cachedThread) {
      Vue.set(state.threadsById, threadId, {
        ...cachedThread,
        commentCount: Math.max(0, (cachedThread.commentCount || 0) + delta),
      });
    }
    Object.keys(state.feeds).forEach((feedScopeKey) => {
      const feed = state.feeds[feedScopeKey];
      if (!feed?.threads) return;
      const threadIndex = feed.threads.findIndex((thread) => thread.id === threadId);
      if (threadIndex !== -1) {
        const updatedThreads = [...feed.threads];
        updatedThreads[threadIndex] = {
          ...updatedThreads[threadIndex],
          commentCount: Math.max(0, (updatedThreads[threadIndex].commentCount || 0) + delta),
        };
        Vue.set(state.feeds, feedScopeKey, { ...feed, threads: updatedThreads });
      }
    });
  },
  applyPinnedChange(state, { id: threadId, pinned }) {
    Object.keys(state.feeds).forEach((feedScopeKey) => {
      const feed = state.feeds[feedScopeKey];
      if (!feed?.threads) return;
      const threadIndex = feed.threads.findIndex((thread) => thread.id === threadId);
      if (threadIndex !== -1) {
        const updatedThreads = [...feed.threads];
        updatedThreads[threadIndex] = { ...updatedThreads[threadIndex], isPinned: pinned };
        Vue.set(state.feeds, feedScopeKey, { ...feed, threads: updatedThreads });
      }
    });
    const cachedThread = state.threadsById[threadId];
    if (cachedThread) {
      Vue.set(state.threadsById, threadId, { ...cachedThread, isPinned: pinned });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
